import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faServer,
  faDatabase,
  faEnvelope,
  faShieldAlt,
  faBrain,
  faCreditCard,
  faMobileAlt,
  faPlayCircle,
  faCloud,
  faMemory,
  faCheckCircle,
  faTimesCircle,
  faExclamationCircle,
  faEye,
  faCheck,
  faSync,
  faFilter,
  faChevronDown,
  faChevronUp,
  faClock,
  faSearch,
  faBug,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import PlatformErrorAPI from "../../services/PlatformErrorAPI";
import ErrorDetailModal from "./ErrorDetailModal";
import LoadingSpinner from "../../common/LoadingSpinner";
import RefreshButton from "../../common/RefreshButton";
import "./css/SystemErrors.css";

// Category display configuration
const CATEGORY_CONFIG = {
  AUTHENTICATION: {
    icon: faShieldAlt,
    color: "#ef4444",
    label: "Authentication & Security",
  },
  DATABASE: {
    icon: faDatabase,
    color: "#f59e0b",
    label: "Database (RDS MySQL)",
  },
  CACHE: {
    icon: faMemory,
    color: "#8b5cf6",
    label: "Cache (ElastiCache Redis)",
  },
  EMAIL_PROCESSING: {
    icon: faEnvelope,
    color: "#3b82f6",
    label: "Email Processing",
  },
  IMAGE_SECURITY: {
    icon: faShieldAlt,
    color: "#ec4899",
    label: "Image Security Pipeline",
  },
  AI_ML_SERVICES: { icon: faBrain, color: "#10b981", label: "AI/ML Services" },
  PAYMENT: { icon: faCreditCard, color: "#f97316", label: "Payment (Stripe)" },
  SMS_PHONE: {
    icon: faMobileAlt,
    color: "#06b6d4",
    label: "SMS & Phone (Twilio)",
  },
  SIMULATION: {
    icon: faPlayCircle,
    color: "#6366f1",
    label: "Simulation Engine",
  },
  INFRASTRUCTURE: { icon: faCloud, color: "#64748b", label: "Infrastructure" },
};

// Display order for the Error Categories grid; unlisted categories sort last.
const CATEGORY_ORDER = [
  "AUTHENTICATION",
  "EMAIL_PROCESSING",
  "IMAGE_SECURITY",
  "SIMULATION",
  "CACHE",
  "AI_ML_SERVICES",
  "SMS_PHONE",
  "PAYMENT",
  "DATABASE",
  "INFRASTRUCTURE",
];
const categoryRank = (category) => {
  const i = CATEGORY_ORDER.indexOf(category);
  return i === -1 ? CATEGORY_ORDER.length : i;
};

const SEVERITY_CONFIG = {
  CRITICAL: {
    color: "#dc2626",
    bgColor: "rgba(220, 38, 38, 0.15)",
    label: "Critical",
  },
  HIGH: {
    color: "#f59e0b",
    bgColor: "rgba(245, 158, 11, 0.15)",
    label: "High",
  },
  MEDIUM: {
    color: "#3b82f6",
    bgColor: "rgba(59, 130, 246, 0.15)",
    label: "Medium",
  },
  LOW: { color: "#6b7280", bgColor: "rgba(107, 114, 128, 0.15)", label: "Low" },
};

// Names match PlatformErrorService.SERVICE_DISPLAY_NAMES; listed in processing order.
const LAMBDA_GROUPS = [
  {
    label: "Email Pipeline",
    icon: faEnvelope,
    services: [
      "Email Ingest Lambda",
      "Email Analysis Lambda",
      "Email Notification Lambda",
    ],
  },
  {
    label: "Image Pipeline",
    icon: faImage,
    services: [
      "Image Security Lambda",
      "Virus Scan Lambda",
      "Image Sanitizer Lambda",
      "Image Analysis Lambda",
      "Image Notification Lambda",
    ],
  },
  {
    label: "Simulation Scheduling",
    icon: faPlayCircle,
    services: ["Schedule Generator Lambda", "Schedule Executor Lambda"],
  },
];

// Request-flow order; unlisted services sort last.
const INFRA_ORDER = [
  "CloudFront CDN",
  "WAF Firewall",
  "API Gateway WebSocket",
  "Spring Boot API",
  "ElastiCache Redis",
  "RDS MySQL",
];
const infraRank = (name) => {
  const i = INFRA_ORDER.indexOf(name);
  return i === -1 ? INFRA_ORDER.length : i;
};

const STATUS_CONFIG = {
  NEW: { color: "#ef4444", icon: faExclamationCircle, label: "New" },
  ACKNOWLEDGED: { color: "#f59e0b", icon: faEye, label: "Acknowledged" },
  RESOLVED: { color: "#10b981", icon: faCheckCircle, label: "Resolved" },
};

function SystemErrors() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [errorList, setErrorList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState(24);
  const [activeView, setActiveView] = useState("overview"); // overview, list
  const [selectedError, setSelectedError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filters
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState(null); // { key, name }
  // User toggles; sections without an entry default to expanded only when they have errors.
  const [sectionOverrides, setSectionOverrides] = useState({});
  const [filterPage, setFilterPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Load summary data
  const loadSummary = useCallback(
    async (hours = selectedTimeRange) => {
      try {
        const data = await PlatformErrorAPI.getErrorSummary(hours);
        setSummary(data);
      } catch (err) {
        console.error("[SystemErrors] Failed to load summary:", err);
        toast.error(t("systemErrors.toast.summaryLoadFailed"));
      }
    },
    [selectedTimeRange],
  );

  // Load error list
  const loadErrorList = useCallback(
    async (hours = selectedTimeRange) => {
      try {
        const data = await PlatformErrorAPI.getErrorList({
          category: filterCategory || undefined,
          severity: filterSeverity || undefined,
          status: filterStatus || undefined,
          sourceService: filterSource?.key,
          hours,
          page: filterPage,
          pageSize: 50,
        });
        setErrorList(data);
      } catch (err) {
        console.error("[SystemErrors] Failed to load error list:", err);
        toast.error(t("systemErrors.toast.listLoadFailed"));
      }
    },
    [
      selectedTimeRange,
      filterCategory,
      filterSeverity,
      filterStatus,
      filterSource,
      filterPage,
    ],
  );

  // Initial load and auto-refresh
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadSummary(), loadErrorList()]);
      setLoading(false);
    };
    loadAll();

    // Auto-refresh every 5 minutes
    const interval = setInterval(() => {
      loadSummary();
      loadErrorList();
    }, 300000);

    return () => clearInterval(interval);
  }, [loadSummary, loadErrorList]);

  // Reload when filters change
  useEffect(() => {
    loadErrorList();
  }, [
    filterCategory,
    filterSeverity,
    filterStatus,
    filterSource,
    filterPage,
    loadErrorList,
  ]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadSummary(), loadErrorList()]);
    setRefreshing(false);
    toast.success(t("systemErrors.toast.dashboardRefreshed"));
  };

  const handleTimeRangeChange = (hours) => {
    setSelectedTimeRange(hours);
    setFilterPage(0);
    loadSummary(hours);
    loadErrorList(hours);
  };

  const handleViewError = async (error) => {
    try {
      const detail = await PlatformErrorAPI.getErrorDetail(
        error.category,
        error.errorId,
      );
      setSelectedError(detail);
      setShowDetailModal(true);
    } catch (err) {
      toast.error(t("systemErrors.toast.detailLoadFailed"));
    }
  };

  const handleAcknowledge = async (category, errorId) => {
    try {
      await PlatformErrorAPI.acknowledgeError(category, errorId);
      toast.success(t("systemErrors.toast.acknowledged"));
      loadSummary();
      loadErrorList();
      setShowDetailModal(false);
    } catch (err) {
      toast.error(t("systemErrors.toast.acknowledgeFailed"));
    }
  };

  const handleResolve = async (category, errorId, notes) => {
    try {
      await PlatformErrorAPI.resolveError(category, errorId, notes);
      toast.success(t("systemErrors.toast.resolved"));
      loadSummary();
      loadErrorList();
      setShowDetailModal(false);
    } catch (err) {
      toast.error(t("systemErrors.toast.resolveFailed"));
    }
  };

  const handleCategoryClick = (category) => {
    setFilterCategory(category);
    setFilterPage(0);
    setActiveView("list");
  };

  const handleServiceClick = (service) => {
    setFilterSource({ key: service.serviceKey, name: service.serviceName });
    setFilterCategory("");
    setFilterSeverity("");
    setFilterStatus("");
    setFilterPage(0);
    setActiveView("list");
  };

  if (loading) {
    return (
      <div className="system-errors loading">
        <LoadingSpinner message="Loading error monitoring data..." />
      </div>
    );
  }

  // Format timestamp for display
  const formatTimestamp = (ts) => {
    if (!ts) return "N/A";
    try {
      const date = new Date(ts);
      return date.toLocaleString();
    } catch {
      return ts;
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case "HEALTHY":
        return { icon: faCheckCircle, color: "#10b981" };
      case "DEGRADED":
        return { icon: faExclamationCircle, color: "#f59e0b" };
      case "DOWN":
        return { icon: faTimesCircle, color: "#ef4444" };
      default:
        return { icon: faExclamationCircle, color: "#6b7280" };
    }
  };

  // ==========================================
  // OVERVIEW RENDER
  // ==========================================
  const renderOverview = () => (
    <div className="system-errors-overview">
      {/* Severity Summary Cards */}
      <div className="severity-summary-grid">
        <div className="severity-card critical">
          <div className="severity-count">{summary?.totalCritical || 0}</div>
          <div className="severity-label">Critical</div>
        </div>
        <div className="severity-card high">
          <div className="severity-count">{summary?.totalHigh || 0}</div>
          <div className="severity-label">High</div>
        </div>
        <div className="severity-card medium">
          <div className="severity-count">{summary?.totalMedium || 0}</div>
          <div className="severity-label">Medium</div>
        </div>
        <div className="severity-card low">
          <div className="severity-count">{summary?.totalLow || 0}</div>
          <div className="severity-label">Low</div>
        </div>
        <div className="severity-card total">
          <div className="severity-count">{summary?.totalErrors || 0}</div>
          <div className="severity-label">Total Errors</div>
        </div>
        <div className="severity-card new-errors">
          <div className="severity-count">{summary?.totalNew || 0}</div>
          <div className="severity-label">Unreviewed</div>
        </div>
      </div>

      {/* Service Health Grid */}
      <div className="section-header">
        <h3>
          <FontAwesomeIcon icon={faServer} /> Service Health
        </h3>
      </div>

      {/* Lambda Functions Group */}
      {(() => {
        const allServices = summary?.serviceHealth || [];
        const lambdas = allServices.filter((s) =>
          s.serviceName?.toLowerCase().includes("lambda"),
        );
        const infra = allServices
          .filter((s) => !s.serviceName?.toLowerCase().includes("lambda"))
          .sort((a, b) => infraRank(a.serviceName) - infraRank(b.serviceName));

        const byName = new Map(lambdas.map((s) => [s.serviceName, s]));
        const lambdaGroups = LAMBDA_GROUPS.map((g) => ({
          ...g,
          items: g.services.map((n) => byName.get(n)).filter(Boolean),
        }));
        const grouped = new Set(LAMBDA_GROUPS.flatMap((g) => g.services));
        const otherLambdas = lambdas.filter((s) => !grouped.has(s.serviceName));
        if (otherLambdas.length > 0) {
          lambdaGroups.push({
            label: "Other",
            icon: faCloud,
            items: otherLambdas,
          });
        }

        const renderServiceCard = (service, idx) => {
          const healthStyle = getHealthIcon(service.status);
          const clickable = service.errorCount > 0 && !!service.serviceKey;
          return (
            <div
              key={service.serviceName || idx}
              className={`service-health-card ${service.status?.toLowerCase()} ${
                service.errorCount > 0 ? "has-errors" : ""
              }`}
              {...(clickable && {
                role: "button",
                tabIndex: 0,
                title: `View ${service.errorCount} error(s) for ${service.serviceName}`,
                onClick: () => handleServiceClick(service),
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleServiceClick(service);
                  }
                },
              })}
            >
              <div className="service-health-header">
                <FontAwesomeIcon
                  icon={healthStyle.icon}
                  style={{ color: healthStyle.color }}
                />
                <span className="service-name">{service.serviceName}</span>
              </div>
              <div className="service-health-stats">
                <span className="stat">
                  <span className="stat-value">{service.errorCount}</span>{" "}
                  errors
                </span>
                {service.criticalCount > 0 && (
                  <span className="stat critical">
                    <span className="stat-value">{service.criticalCount}</span>{" "}
                    critical
                  </span>
                )}
                <span className="stat">
                  <span className="stat-value">
                    {service.errorRate.toFixed(1)}
                  </span>
                  /hr
                </span>
              </div>
              {service.lastErrorTimestamp && (
                <div className="service-health-footer">
                  <FontAwesomeIcon icon={faClock} />
                  <span>{formatTimestamp(service.lastErrorTimestamp)}</span>
                </div>
              )}
            </div>
          );
        };

        const sumErrors = (items) =>
          items.reduce((sum, s) => sum + (s.errorCount || 0), 0);
        const isExpanded = (id, errors) => sectionOverrides[id] ?? errors > 0;
        const toggleProps = (id, errors) => {
          const toggle = () =>
            setSectionOverrides((prev) => ({
              ...prev,
              [id]: !(prev[id] ?? errors > 0),
            }));
          return {
            role: "button",
            tabIndex: 0,
            "aria-expanded": isExpanded(id, errors),
            onClick: toggle,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle();
              }
            },
          };
        };
        const renderSectionStatus = (errors) =>
          errors > 0 ? (
            <span className="section-status errors">{errors} errors</span>
          ) : (
            <span className="section-status ok">
              <FontAwesomeIcon icon={faCheckCircle} /> No errors
            </span>
          );
        const renderChevron = (expanded) => (
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="section-chevron"
          />
        );

        const lambdaErrors = sumErrors(lambdas);
        const lambdaExpanded = isExpanded("lambda", lambdaErrors);
        const infraErrors = sumErrors(infra);
        const infraExpanded = isExpanded("infra", infraErrors);

        return (
          <div className="service-health-grouped">
            <div
              className={`service-health-group ${lambdaExpanded ? "" : "collapsed"}`}
            >
              <div
                className="service-health-group-header"
                {...toggleProps("lambda", lambdaErrors)}
              >
                <FontAwesomeIcon icon={faCloud} className="group-icon lambda" />
                <h4>Lambda Functions</h4>
                {renderSectionStatus(lambdaErrors)}
                <span className="group-count">{lambdas.length} services</span>
                {renderChevron(lambdaExpanded)}
              </div>
              {lambdaExpanded &&
                lambdaGroups
                  .filter((g) => g.items.length > 0)
                  .map((g) => {
                    const id = `lambda:${g.label}`;
                    const groupErrors = sumErrors(g.items);
                    const expanded = isExpanded(id, groupErrors);
                    return (
                      <div
                        key={g.label}
                        className={`service-health-subgroup ${expanded ? "" : "collapsed"}`}
                      >
                        <div
                          className="service-health-subgroup-header"
                          {...toggleProps(id, groupErrors)}
                        >
                          <FontAwesomeIcon icon={g.icon} />
                          <span className="subgroup-label">{g.label}</span>
                          {renderSectionStatus(groupErrors)}
                          {renderChevron(expanded)}
                        </div>
                        {expanded && (
                          <div className="service-health-grid">
                            {g.items.map(renderServiceCard)}
                          </div>
                        )}
                      </div>
                    );
                  })}
            </div>

            <div
              className={`service-health-group ${infraExpanded ? "" : "collapsed"}`}
            >
              <div
                className="service-health-group-header"
                {...toggleProps("infra", infraErrors)}
              >
                <FontAwesomeIcon icon={faServer} className="group-icon infra" />
                <h4>Infrastructure</h4>
                {renderSectionStatus(infraErrors)}
                <span className="group-count">{infra.length} services</span>
                {renderChevron(infraExpanded)}
              </div>
              {infraExpanded && (
                <div className="service-health-grid">
                  {infra.map(renderServiceCard)}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Error Categories Grid */}
      <div className="section-header">
        <h3>
          <FontAwesomeIcon icon={faBug} /> Error Categories
        </h3>
      </div>
      <div className="category-grid">
        {[...(summary?.categories || [])]
          .sort((a, b) => categoryRank(a.category) - categoryRank(b.category))
          .map((cat, idx) => {
            const config =
              CATEGORY_CONFIG[cat.category] || CATEGORY_CONFIG.INFRASTRUCTURE;
            return (
              <div
                key={idx}
                className={`category-card ${cat.count > 0 ? "has-errors" : ""}`}
                onClick={() =>
                  cat.count > 0 && handleCategoryClick(cat.category)
                }
                style={{ cursor: cat.count > 0 ? "pointer" : "default" }}
              >
                <div className="category-card-header">
                  <div
                    className="category-icon"
                    style={{ color: config.color }}
                  >
                    <FontAwesomeIcon icon={config.icon} />
                  </div>
                  <div className="category-info">
                    <h4>{config.label}</h4>
                    <span className="category-count">{cat.count} errors</span>
                  </div>
                </div>
                {cat.count > 0 && (
                  <div className="category-severity-badges">
                    {cat.criticalCount > 0 && (
                      <span className="badge critical">
                        {cat.criticalCount} Critical
                      </span>
                    )}
                    {cat.highCount > 0 && (
                      <span className="badge high">{cat.highCount} High</span>
                    )}
                    {cat.mediumCount > 0 && (
                      <span className="badge medium">
                        {cat.mediumCount} Medium
                      </span>
                    )}
                    {cat.lowCount > 0 && (
                      <span className="badge low">{cat.lowCount} Low</span>
                    )}
                  </div>
                )}
                {cat.latestMessage && (
                  <div className="category-latest">
                    <span className="latest-label">Latest:</span>
                    <span className="latest-message">{cat.latestMessage}</span>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );

  // ==========================================
  // ERROR LIST RENDER
  // ==========================================
  const renderErrorList = () => (
    <div className="system-errors-list">
      {/* Filters */}
      <div className="filters-bar">
        <button
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FontAwesomeIcon icon={faFilter} />
          <span>Filters</span>
          <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} />
        </button>

        {(filterCategory || filterSeverity || filterStatus || filterSource) && (
          <div className="active-filters">
            {filterSource && (
              <span
                className="filter-chip"
                onClick={() => {
                  setFilterSource(null);
                  setFilterPage(0);
                }}
              >
                {filterSource.name} ×
              </span>
            )}
            {filterCategory && (
              <span
                className="filter-chip"
                onClick={() => setFilterCategory("")}
              >
                {CATEGORY_CONFIG[filterCategory]?.label || filterCategory} ×
              </span>
            )}
            {filterSeverity && (
              <span
                className="filter-chip"
                onClick={() => setFilterSeverity("")}
              >
                {filterSeverity} ×
              </span>
            )}
            {filterStatus && (
              <span className="filter-chip" onClick={() => setFilterStatus("")}>
                {filterStatus} ×
              </span>
            )}
            <button
              className="clear-filters"
              onClick={() => {
                setFilterCategory("");
                setFilterSeverity("");
                setFilterStatus("");
                setFilterSource(null);
                setFilterPage(0);
              }}
            >
              Clear All
            </button>
          </div>
        )}

        <div className="list-count">
          {errorList?.totalCount || 0} errors found
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Category</label>
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setFilterPage(0);
              }}
            >
              <option value="">All Categories</option>
              {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
                <option key={key} value={key}>
                  {val.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Severity</label>
            <select
              value={filterSeverity}
              onChange={(e) => {
                setFilterSeverity(e.target.value);
                setFilterPage(0);
              }}
            >
              <option value="">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setFilterPage(0);
              }}
            >
              <option value="">All Statuses</option>
              <option value="NEW">New</option>
              <option value="ACKNOWLEDGED">Acknowledged</option>
              <option value="RESOLVED">Resolved</option>
            </select>
          </div>
        </div>
      )}

      {/* Error Table */}
      <div className="error-table-container">
        <table className="error-table">
          <thead>
            <tr>
              <th>Severity</th>
              <th>Category</th>
              <th>Source</th>
              <th>Error Type</th>
              <th>Message</th>
              <th>Count</th>
              <th>Time</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(errorList?.errors || []).map((error, idx) => {
              const sevConfig =
                SEVERITY_CONFIG[error.severity] || SEVERITY_CONFIG.LOW;
              const catConfig =
                CATEGORY_CONFIG[error.category] ||
                CATEGORY_CONFIG.INFRASTRUCTURE;
              const statusConfig =
                STATUS_CONFIG[error.status] || STATUS_CONFIG.NEW;

              return (
                <tr
                  key={idx}
                  className={`error-row ${error.severity?.toLowerCase()}`}
                >
                  <td>
                    <span
                      className="severity-badge"
                      style={{
                        color: sevConfig.color,
                        backgroundColor: sevConfig.bgColor,
                      }}
                    >
                      {sevConfig.label}
                    </span>
                  </td>
                  <td>
                    <span className="category-label">
                      <FontAwesomeIcon
                        icon={catConfig.icon}
                        style={{ color: catConfig.color }}
                      />
                      {catConfig.label}
                    </span>
                  </td>
                  <td className="source-cell">{error.sourceService || "—"}</td>
                  <td className="error-type-cell">{error.errorType || "—"}</td>
                  <td className="message-cell" title={error.message}>
                    {error.message?.substring(0, 120) || "—"}
                  </td>
                  <td className="count-cell">
                    {error.occurrenceCount > 1 ? (
                      <span className="occurrence-badge">
                        ×{error.occurrenceCount}
                      </span>
                    ) : (
                      "1"
                    )}
                  </td>
                  <td className="time-cell">
                    {formatTimestamp(error.timestamp)}
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ color: statusConfig.color }}
                    >
                      <FontAwesomeIcon icon={statusConfig.icon} />
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="action-btn view"
                      onClick={() => handleViewError(error)}
                      title="View Details"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    {error.status === "NEW" && (
                      <button
                        className="action-btn acknowledge"
                        onClick={() =>
                          handleAcknowledge(error.category, error.errorId)
                        }
                        title="Acknowledge"
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {(!errorList?.errors || errorList.errors.length === 0) && (
              <tr>
                <td colSpan="9" className="no-errors">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>
                    No errors found for the selected filters and time range.
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {errorList?.totalCount > 50 && (
        <div className="pagination">
          <button
            disabled={filterPage === 0}
            onClick={() => setFilterPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>
          <span>
            Page {filterPage + 1} of {Math.ceil(errorList.totalCount / 50)}
          </span>
          <button
            disabled={!errorList.hasMore}
            onClick={() => setFilterPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  // ==========================================
  // MAIN RENDER
  // ==========================================
  return (
    <div className="system-errors">
      {/* Sub-navigation */}
      <div className="system-errors-nav">
        <div className="nav-tabs">
          <button
            className={`nav-tab ${activeView === "overview" ? "active" : ""}`}
            onClick={() => setActiveView("overview")}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>Overview</span>
          </button>
          <button
            className={`nav-tab ${activeView === "list" ? "active" : ""}`}
            onClick={() => setActiveView("list")}
          >
            <FontAwesomeIcon icon={faSearch} />
            <span>Error Log</span>
            {summary?.totalNew > 0 && (
              <span className="new-count-badge">{summary.totalNew}</span>
            )}
          </button>
        </div>
        <div className="nav-controls">
          <div className="time-selector">
            <select
              value={selectedTimeRange}
              onChange={(e) => handleTimeRangeChange(Number(e.target.value))}
            >
              <option value={1}>Last Hour</option>
              <option value={6}>Last 6 Hours</option>
              <option value={24}>Last 24 Hours</option>
              <option value={168}>Last Week</option>
              <option value={720}>Last 30 Days</option>
            </select>
          </div>
          <RefreshButton onClick={handleRefresh} loading={refreshing} />
        </div>
      </div>

      {/* Content */}
      <div className="system-errors-content">
        {activeView === "overview" ? renderOverview() : renderErrorList()}
      </div>

      {/* Error Detail Modal */}
      {showDetailModal && selectedError && (
        <ErrorDetailModal
          error={selectedError}
          onClose={() => setShowDetailModal(false)}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
          categoryConfig={CATEGORY_CONFIG}
          severityConfig={SEVERITY_CONFIG}
          statusConfig={STATUS_CONFIG}
        />
      )}
    </div>
  );
}

export default SystemErrors;
